import React from 'react';
import styled from 'styled-components';
import type { KeyedMutator } from 'swr';
import { isSignedIn } from '@peloton/auth/models/user';
import { getUser } from '@peloton/auth/redux';
import { media } from '@peloton/styles/breakpoints';
import { track } from '@engage/analytics/actions';
import { If } from '@engage/conditional-render/If';
import { useReduxAction, useReduxState } from '@engage/redux/hooks';
import {
  TAGS_MODAL_NAME,
  TAG_NAME_PARAM,
  TAGS_VIEW_PARAM,
  TAG_BROWSE_CATEGORY_PARAM,
  VIEW_STACK,
} from '@engage/tags/shared';
import { useModalLabel } from '@members/copy/copy.generated';
import type { CopyIDs } from '@members/copy/CopyIDs';
import { ModalLabel } from '@members/copy/models.generated/ModalLabel';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import { ConnectedModal } from '@members/modal/ConnectedModal';
import { trackCancelReplaceTagFlow } from '../analytics/events';
import { useAnalyticsHeadersFromView } from '../analytics/hooks';
import { useLastTagSearch, useSelectedTagName } from '../hooks';
import { getTagViewFromLocation, getLastTagView } from '../selectors';
import { TagModalView } from '../shared';
import { TagsModalView } from './TagsModalView';

export const closeModalHandler = (
  clearLastTagSearch: KeyedMutator<string>,
  sendAnalytics: () => void,
) => {
  sendAnalytics();
  clearLastTagSearch('');
};

export const TagsModal: React.FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();
  const trackAnalytics = useReduxAction(track);
  const { setLastTagSearch } = useLastTagSearch();
  const { selectedTagName } = useSelectedTagName();
  const tagName = selectedTagName;
  const view = getTagViewFromLocation(router.query);
  const lastView = getLastTagView(router.query) as TagModalView | undefined;
  const analyticsHeaderProps = useAnalyticsHeadersFromView(lastView);

  const onCloseAnalytics = React.useCallback(() => {
    if (view === TagModalView.ReplaceTag) {
      trackAnalytics(trackCancelReplaceTagFlow(tagName, analyticsHeaderProps));
    }
  }, [view, tagName]);
  const onCloseModal = React.useCallback(
    () => closeModalHandler(setLastTagSearch, onCloseAnalytics),
    [onCloseAnalytics],
  );
  const ariaLabelId = useModalLabel(ModalLabel.Tags) as CopyIDs<typeof ModalLabel>;
  const user = useReduxState(getUser);
  const [showErrorToast, setShowErrorToast] = React.useState(false);
  const showMobileCloseButton = view !== TagModalView.TagDetails && !showErrorToast;

  return (
    <If condition={isSignedIn(user)}>
      <StyledModal
        modalNames={{
          [TAGS_MODAL_NAME]: {
            ariaLabelId,
            component: (
              <TagsModalView
                showErrorToast={showErrorToast}
                setShowErrorToast={setShowErrorToast}
              />
            ),
            showMobileCloseButton,
            modelIdentifiers: [
              TAGS_VIEW_PARAM,
              TAG_NAME_PARAM,
              TAG_BROWSE_CATEGORY_PARAM,
              VIEW_STACK,
            ],
            onCloseModal,
          },
        }}
      />
    </If>
  );
};

// eslint:disable:inline-spaces-values
const StyledModal = styled(ConnectedModal)`
  ${media.tablet`
    max-width: 560px;
    max-height: 792px;
  `}
`;
// eslint:enable:inline-spaces-values
StyledModal.displayName = 'StyledModal';
